
/* eslint-disable @typescript-eslint/camelcase */
import { Options, Vue } from "vue-class-component";
import TableDetail from "../detail/table-detail.vue";
import { Pagination } from "@/domain/entities/Pagination";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import { ShipmentListRequest, CourierSummaryRequest } from "@/data/payload/api/PickupDashboardRequests";
import { ellipsisString, whatsapp, formatByCountry } from "@/app/infrastructures/misc/Utils";
import DatePickerV2 from "@/app/ui/components/date-picker-v2/index.vue";
import formatDateNumber from "@/app/infrastructures/misc/common-library/FormatDateNumber";
import { formatDate } from "@/app/infrastructures/misc/Utils";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import ErrorMessage from "@/app/ui/components/error-message/index.vue";
import { INTERNAL_SERVER_ERROR as INTERNAL_SERVER_ERROR_CONST } from "@/app/infrastructures/misc/Constants";

type Pill = "today" | "yesterday" | "custom";

@Options({
  components: { TableDetail, DatePickerV2, ErrorMessage }
})
export default class PickupDashboardMonitoringCourierDetailCRRCNC extends Vue {
[x: string]: any;
  INTERNAL_SERVER_ERROR = INTERNAL_SERVER_ERROR_CONST as string;
  formatPhoneNumber = formatPhoneNumber;
  sortBy = "status_at" as string;
  sortDir = "desc" as "asc" | "desc" | "";
  pagination = new Pagination(1, 20, 0);
  listData: any[] = [];
  isLoading = false;
  error = "" as string;
  lastFetchKey = "";
  pendingKey = "";
  readyForUserSort = false;
  showImagePreview = false;
  images: Array<{ image: string; url: string }> = [];
  image = "" as string;
  activePill: Pill = "today";
  customDate: Date | null = null;
  startDateStr = "";
  endDateStr = "";

  get columns() {
    return [
      {
        key: "no",
        name: "pickupDashboard.column.no",
        styleHead: "w-16 text-left"
      },
      {
        key: "sender_name",
        name: "pickupDashboard.column.senderName",
        sortable: true
      },
      {
        key: "shipment_type",
        name: "pickupDashboard.column.shipmentType",
        render: undefined
      },
      {
        key: "total_shipment",
        name: "pickupDashboard.column.totalShipment",
        sortable: true
      },
      {
        key: "status_at",
        name: "pickupDashboard.column.cancelDate",
        sortable: true
      },
      {
        key: "cancel_reason",
        name: "pickupDashboard.column.cancelReason",
        render: undefined
      },
      {
        key: "bukti",
        name: "pickupDashboard.column.proof",
        styleHead: "text-center",
        styleCustom: "justify-center",
        render: undefined
      }
    ];
  }

  get courierId() {
    return Number(this.$route.params.id || 1);
  }

  get startIndex() {
    return (this.pagination.page - 1) * this.pagination.limit;
  }

  get pagedData() {
    return this.listData;
  }

  mounted() {
    this.applyPillDate();
    this.fetchData();
  }

  pillClass(active: boolean) {
    return active
      ? "px-5 py-1 rounded-3xl bg-red-lp-600 text-red-lp-100 text-16px"
      : "px-5 py-1 rounded-3xl border border-gray-lp-400 text-gray-lp-300 text-16px bg-gray-lp-200";
  }

  setPill(p: Pill) {
    this.activePill = p;
    if (p !== "custom") {
      this.customDate = null;
    }
    this.applyPillDate();
    this.updateRouteQuery();
    this.pagination.page = 1;
    this.fetchData();
    this.fetchSummary();
  }

  async fetchSummary() {
    const cf = PickupDashboardController.currentFilter;
    const params = new CourierSummaryRequest({
      startDate: this.startDateStr,
      endDate: this.endDateStr,
      courierId: Number(cf.courierId || this.courierId),
      status: "CRRCNC",
      q: String(cf.q || ""),
      shipmentType: String(cf.shipmentType || "")
    });
    await PickupDashboardController.getCourierSummary(params);
  }

  onSelectCustomDate(date: Date) {
    this.customDate = date;
    this.applyPillDate();
    this.updateRouteQuery();
    this.pagination.page = 1;
    this.fetchData();
    this.fetchSummary();
  }

  applyPillDate() {
    const today = new Date();
    let base = today;
    if (this.activePill === "yesterday") {
      const y = new Date(today);
      y.setDate(today.getDate() - 1);
      base = y;
    }
    if (this.activePill === "custom" && this.customDate) {
      base = this.customDate;
    }
    const d = formatDateNumber(base);
    this.startDateStr = d;
    this.endDateStr = d;
  }

  updateRouteQuery() {
    try {
      const query = {
        ...this.$route.query,
        start_date: this.startDateStr,
        end_date: this.endDateStr
      } as Record<string, string>;
      this.$router.replace({ path: this.$route.path, query });
    } catch (_) {
      // ignore
    }
  }

  onSort(key: string) {
    if (this.sortBy !== key) {
      this.sortBy = key;
      this.sortDir = "asc";
    } else if (this.sortDir === "asc") {
      this.sortDir = "desc";
    } else {
      this.sortBy = "";
      this.sortDir = "";
    }
    this.fetchData();
  }

  onOpenWa(phone: string) {
    whatsapp(phone);
  }

  get isFiltering() {
    const q = this.$route.query.q;
    const shipmentType = this.$route.query.shipment_type;
    const start = this.$route.query.start_date;
    const end = this.$route.query.end_date;
    return Boolean(q || shipmentType || start || end);
  }

  formatShipmentType(val: string) {
    if (!val) return "-";
    const lower = String(val).toLowerCase();
    const normalized = lower.replace(/_/g, " ");
    return normalized.replace(/\b\w/g, (l) => l.toUpperCase());
  }

  formatCancelDate(item: any) {
    const date =
      item.cancelled_at ||
      item.canceled_at ||
      item.cancel_at ||
      item.cancellation_date ||
      item.status_at ||
      item.status_at ||
      "";
    if (!date) return "-";
    try {
      const d = new Date(date);
      const day = d.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
      const time = d.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit"
      });
      return `${day}, ${time}`;
    } catch (_) {
      return String(date);
    }
  }

  getCancelReason(item: any) {
    return item.cancel_reason || item.cancel_note || item.reason || "-";
  }

  onSetPage(p: number) {
    this.pagination.page = p;
    this.fetchData();
  }

  onShowProof(item: any) {
    const urls = this.getProofImages(item);
    this.images = urls.map((u: string) => ({ image: u, url: u }));
    this.image = this.images[0]?.image || "";
    this.showImagePreview = true;
  }

  closeImagePreview() {
    this.images = [];
    this.image = "";
    this.showImagePreview = false;
  }

  getProofImages(item: any): string[] {
    const arr = [] as string[];
    const candidates = [
      item.cancel_proof_urls,
      item.cancel_proof,
      item.proofs,
      item.photos,
      item.images
    ];
    for (const c of candidates) {
      if (Array.isArray(c) && c.length)
        return c.filter((u: any) => typeof u === "string");
      if (typeof c === "string" && c) arr.push(c);
    }
    return arr;
  }

  async fetchData() {
    await this.safeRunFetch("CRRCNC");
  }

  private buildFetchKey() {
    return [
      this.courierId,
      this.startDateStr,
      this.endDateStr,
      this.pagination.page,
      this.pagination.limit,
      this.sortBy,
      this.sortDir
    ].join("|");
  }

  private isArgsMissing() {
    return !this.startDateStr || !this.endDateStr || !this.courierId;
  }

  private shouldSkipFetch(key: string) {
    if (this.pendingKey === key) return true;
    if (this.lastFetchKey === key) return true;
    return false;
  }

  private createListRequest(status: string) {
    const cf = PickupDashboardController.currentFilter;
    return new ShipmentListRequest({
      courierId: this.courierId,
      startDate: this.startDateStr,
      endDate: this.endDateStr,
      status,
      q: String(cf.q || ""),
      shipmentType: String(cf.shipmentType || ""),
      page: this.pagination.page,
      limit: this.pagination.limit,
      sortBy: this.sortDir.toUpperCase(),
      orderBy: this.sortBy
    });
  }

  private updateListFromResponse(res: any) {
    this.listData = Array.isArray(res.data)
      ? res.data.map((d: any) => ({ ...d, is_checked: Boolean(d.is_checked) }))
      : [];
    this.pagination = new Pagination(
      res.pagination.page || this.pagination.page,
      res.pagination.limit || this.pagination.limit,
      res.pagination.totalData || 0
    );
  }

  private async safeRunFetch(status: string) {
    try {
      this.error = "";
      if (this.isArgsMissing()) return;
      const key = this.buildFetchKey();
      if (this.shouldSkipFetch(key)) return;
      this.isLoading = true;
      this.pendingKey = key;
      this.lastFetchKey = key;
      const params = this.createListRequest(status);
      const res = await PickupDashboardController.getShipmentList(params);
      this.updateListFromResponse(res);
    } catch (e) {
      const msg =
        (e as any)?.message || String(this.$t("pickupDashboard.systemError"));
      this.error = msg;
    } finally {
      this.isLoading = false;
      this.pendingKey = "";
      this.readyForUserSort = true;
    }
  }

  fmtQty(val: number | string) {
    return formatByCountry(Number(val || 0), { type: "quantity" });
  }

  ellipsis(val: string) {
    return ellipsisString(val, 70);
  }
}
